import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'motion/react';
import { useSiteSettings } from '@/lib/siteSettings';

const services = [
  'Trading',
  'Procurement',
  'Logistics',
  'Freight Forwarding',
  'Clearing & Forwarding',
];

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { settings } = useSiteSettings();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full h-screen min-h-[700px] overflow-hidden">
      {/* Background Image with parallax */}
      <motion.div className="absolute inset-0" style={{ scale: bgScale, y: bgY }}>
        <img
          src="https://readdy.ai/api/search-image?query=Epic%20cinematic%20aerial%20view%20of%20massive%20container%20ship%20navigating%20through%20international%20shipping%20port%20at%20golden%20hour%20with%20warm%20amber%20and%20deep%20navy%20sky%20rich%20golden%20light%20reflecting%20on%20water%20industrial%20cranes%20and%20stacked%20containers%20global%20trade%20logistics%20powerful%20atmosphere%20ultra%20wide%20professional%20photography&width=1600&height=900&seq=bekaali-hero-v4-gold&orientation=landscape&nocache=true"
          alt="BEKAALI Global Trade & Logistics"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay for text contrast */}
        <div className="absolute inset-0 bg-primary-950/80"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/30"></div>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center pt-24 md:pt-28">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-4xl">
            {/* Service pills — what we do, immediately visible */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={loaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap items-center gap-2 mb-8"
            >
              {services.map((svc, i) => (
                <span
                  key={svc}
                  className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border border-white/20 text-white/80 bg-white/5 backdrop-blur-sm"
                >
                  {svc}
                </span>
              ))}
            </motion.div>

            {/* Main headline — bold, uppercase, Helmora-style */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={loaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-white leading-[1.05] mb-6 uppercase tracking-tight"
            >
              Your Gateway
              <br />
              To <span className="text-accent-400">Global Markets</span>
            </motion.h1>

            {/* One-liner that explains exactly what Bekaali does */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={loaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-base md:text-lg text-white/70 max-w-2xl mb-10 leading-relaxed"
            >
              BEKAALI connects East African businesses with premium Chinese manufacturing and global supply chains — delivering end-to-end trade, procurement, and logistics solutions that keep your business moving.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={loaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <Link
                to="/contact"
                className="px-8 py-4 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/20"
              >
                Start Your Shipment
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold text-sm rounded-full hover:bg-white/20 border border-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Explore Our Services
              </Link>
            </motion.div>

            {/* Quick stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={loaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="flex flex-wrap items-center gap-8 md:gap-12 mt-14 pt-8 border-t border-white/10"
            >
              {[
                { number: '5+', label: 'Years Experience' },
                { number: '200+', label: 'Global Partners' },
                { number: '30+', label: 'Countries Served' },
                { number: '3,000+', label: 'Shipments Delivered' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-2">
                  <span className="text-2xl md:text-3xl font-heading font-bold text-accent-400">{stat.number}</span>
                  <span className="text-xs text-white/40 max-w-[80px] leading-tight uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Helmora-style green scroll-down chevron */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500/90 backdrop-blur-sm cursor-pointer hover:bg-emerald-400 transition-colors duration-300"
        >
          <i className="ri-arrow-down-line text-white text-lg"></i>
        </motion.div>
      </motion.div>
    </section>
  );
}